//@flow
type NameSortData = {
  filename: string,
  isDirectory: boolean,
  isAscendSelector: boolean
}

type SizeSortData = {
  size: number,
  isDirectory: boolean,
  isAscendSelector: boolean
}

type CustomSortData = {
  isDirectory: boolean,
  isAscendSelector: boolean,
  [key: string]: string
}

type FileData = {
  path: string,
  [key: string]: any
}

export default class FileManager {
  static FILE_TYPES = {
    AUDIO: 'audio',
    DIRECTORY: 'dir',
    UNKNOWN: 'unknown'
  }

  static ASCEND_SELECTOR = {
    '...': {
      path: '',
      filename: '...',
      size: null,
      lastUpdated: null,
      isDirectory: false,
      isAscendSelector: true
    }
  }

  static getHierarchyStructure = (data: FileData[], pathArray: string[]) => {
    const result = data.reduce<{ [key: string]: FileData }>(
      (acc, file) => {
        const path = pathArray.join('/')
        const isRootPath = !pathArray.length

        let filePath = FileManager._normalizePath(file.path)

        if (path !== '' && path !== '/') {
          const matchPath = FileManager._matchPath(filePath, path)

          if (matchPath === null) {
            return acc
          }

          filePath = matchPath
        }

        const isDirectory = filePath.includes('/')
        const filename = filePath.split('/')[0]
        const extension = FileManager.getExtension(filename)

        acc[filename] = {
          ...(isDirectory ? null : file),
          path: file.path,
          filename,
          isDirectory,
          fileType: isDirectory ? FileManager.FILE_TYPES.DIRECTORY : FileManager.getFileType(extension),
          size: isDirectory || !file.size ? null : Number(file.size),
          lastUpdated: isDirectory ? null : file.lastUpdated
        }

        return acc
      },
      { ...FileManager.ASCEND_SELECTOR }
    )

    return Object.keys(result).map<FileData>(key => result[key])
  }

  static getExtension = (filePath: string): string => {
    const extension = filePath.match(/[.].+$/gi)
    return (extension && extension[0]) || ''
  }

  static getFileIcon = (fileType: string) => {
    switch (fileType) {
      case FileManager.FILE_TYPES.AUDIO:
        return 'music'
      default:
        return 'file'
    }
  }

  static getFileType = (extension: string) => {
    if (/mp3|wav|ogg$/i.test(extension)) {
      return FileManager.FILE_TYPES.AUDIO
    }
    return FileManager.FILE_TYPES.UNKNOWN
  }

  static sortNameColumn = (a: NameSortData, b: NameSortData, isDesc: boolean) => {
    // Make ascend selector be always on top
    if (a.isAscendSelector || b.isAscendSelector) {
      return isDesc
        ? FileManager._compareBooleans(a.isAscendSelector, b.isAscendSelector)
        : FileManager._compareBooleans(b.isAscendSelector, a.isAscendSelector)
    }

    // Compare filenames of same type, i.e directory <-> directory / file <-> file
    if ((a.isDirectory && b.isDirectory) || (!a.isDirectory && !b.isDirectory)) {
      return FileManager._compareStrings(a.filename, b.filename)
    } else {
      // Make directory be always on top after ascend selector
      return isDesc
        ? FileManager._compareBooleans(a.isDirectory, b.isDirectory)
        : FileManager._compareBooleans(b.isDirectory, a.isDirectory)
    }
  }

  static sortSizeColumn = (a: SizeSortData, b: SizeSortData, isDesc: boolean) => {
    if (a.isAscendSelector || b.isAscendSelector) {
      return isDesc
        ? FileManager._compareBooleans(a.isAscendSelector, b.isAscendSelector)
        : FileManager._compareBooleans(b.isAscendSelector, a.isAscendSelector)
    }

    // Compare filenames of same type, i.e directory <-> directory / file <-> file
    if (a.size && b.size) {
      return FileManager._compareNumbers(a.size, b.size)
    } else {
      // Make directory be always on top after ascend selector
      return isDesc
        ? FileManager._compareBooleans(a.isDirectory, b.isDirectory)
        : FileManager._compareBooleans(b.isDirectory, a.isDirectory)
    }
  }

  static sortCustomColumn = (key: string) => (a: CustomSortData, b: CustomSortData, isDesc: boolean) => {
    if (a.isAscendSelector || b.isAscendSelector) {
      return isDesc
        ? FileManager._compareBooleans(a.isAscendSelector, b.isAscendSelector)
        : FileManager._compareBooleans(b.isAscendSelector, a.isAscendSelector)
    }

    // Compare custom values as string
    if (a[key] && b[key]) {
      return FileManager._compareStrings(a[key], b[key])
    } else {
      // Make directory be always on top after ascend selector
      return isDesc
        ? FileManager._compareBooleans(a.isDirectory, b.isDirectory)
        : FileManager._compareBooleans(b.isDirectory, a.isDirectory)
    }
  }

  static formatFileSize = (size: number) => {
    if (size > 1e6) {
      return `${Math.round((size / 1e6) * 100) / 100} MB`
    }
    if (size > 1e3) {
      return `${Math.round((size / 1e3) * 100) / 100} kB`
    }
    return `${Math.round(size * 100) / 100} B`
  }

  static _compareBooleans = (a: boolean, b: boolean) => Number(a) - Number(b)

  static _compareNumbers = (a: number, b: number) => a - b

  static _compareStrings = (a: string, b: string) => {
    if (a < b) return -1
    if (a > b) return 1
    return 0
  }

  static _matchPath = (path: string, pathSegment: string): string | null => {
    const pathRegex = new RegExp(`^(${pathSegment})/|(.+$)`, 'ig')
    const result = path.match(pathRegex)

    return (result && result[1]) || null
  }

  static _normalizePath = (path: string): string => path.replace(/(^\/)|(\/$)/g, '')
}
